import { useCallback, useState } from 'react'

/**
 * Shared multi-select state for the entity-merge (de-duplication) flow, used by
 * the three collection list surfaces (People / Places / generic). Hoisting the
 * selection into a hook keeps the logic in one place and — critically — keeps
 * the selection alive across search-query / filter changes and across
 * opening/closing the non-destructive peek modal. Selection is cleared ONLY on
 * an explicit "Clear", on exiting merge mode, or (Stage 6) a successful merge —
 * never as a side effect of filtering or peeking.
 *
 * The selection is capped at MERGE_MAX; `toggle` refuses to add past the cap so
 * the invariant holds without the caller having to guard every call site. A
 * merge needs at least MERGE_MIN candidates.
 */

export const MERGE_MIN = 2
export const MERGE_MAX = 10

export interface MergeSelectionOptions {
    /** Minimum selection needed for the action (default MERGE_MIN = 2). */
    min?: number
    /** Maximum selectable; additions past it are refused (default MERGE_MAX = 10). Pass Infinity for uncapped. */
    max?: number
}

export interface MergeSelection {
    /** Whether the surface is in "select to merge" mode (checkboxes visible). */
    mergeMode: boolean
    /** The currently-selected collection_item ids. */
    selected: Set<number>
    /** `selected.size` — convenience for the action bar. */
    count: number
    /** True once the selection has hit `max` (used to hint / disable). */
    atCap: boolean
    /** The instance's minimum selection (defaults to MERGE_MIN). */
    min: number
    /** The instance's maximum selection (defaults to MERGE_MAX). */
    max: number
    isSelected: (id: number) => boolean
    /** Add/remove an id. Adding past `max` is a no-op. */
    toggle: (id: number) => void
    /** Replace the whole selection with `ids`, bounded by `max` (keeps merge mode on). */
    selectAll: (ids: number[]) => void
    /** Drop the whole selection (keeps merge mode on). */
    clear: () => void
    enterMergeMode: () => void
    /** Leave merge mode AND drop the selection (an explicit abandon). */
    exitMergeMode: () => void
    toggleMergeMode: () => void
}

export function useMergeSelection(options: MergeSelectionOptions = {}): MergeSelection {
    const { min = MERGE_MIN, max = MERGE_MAX } = options
    const [mergeMode, setMergeMode] = useState(false)
    const [selected, setSelected] = useState<Set<number>>(new Set())

    const isSelected = useCallback((id: number) => selected.has(id), [selected])

    const toggle = useCallback((id: number) => {
        setSelected((prev) => {
            const next = new Set(prev)
            if (next.has(id)) {
                next.delete(id)
            } else {
                if (next.size >= max) return prev
                next.add(id)
            }
            return next
        })
    }, [max])

    const selectAll = useCallback((ids: number[]) => {
        setSelected(new Set(ids.slice(0, max)))
    }, [max])

    const clear = useCallback(() => setSelected(new Set()), [])

    const enterMergeMode = useCallback(() => setMergeMode(true), [])

    const exitMergeMode = useCallback(() => {
        setMergeMode(false)
        setSelected(new Set())
    }, [])

    const toggleMergeMode = useCallback(() => {
        setMergeMode((prev) => {
            if (prev) {
                // Leaving merge mode abandons any in-progress selection.
                setSelected(new Set())
                return false
            }
            return true
        })
    }, [])

    return {
        mergeMode,
        selected,
        count: selected.size,
        atCap: selected.size >= max,
        min,
        max,
        isSelected,
        toggle,
        selectAll,
        clear,
        enterMergeMode,
        exitMergeMode,
        toggleMergeMode,
    }
}
